import {
  randomItem,
  type QueriesObject,
  type QueryResults,
  type System,
} from '../../odie';
import { PowerBlockSpawnEffect } from '../actions/effects/PowerBlockSpawnEffect';
import { BasicBlockComponent } from '../components/BasicBlockComponent';
import { BlockComponent } from '../components/BlockComponent';
import { PhaseComponent } from '../components/PhaseComponent';
import type { BasicBlockProps, BlockId, PowerBlockType } from '../defs/block';
import { blockPropsMap } from '../defs/block';
import { config } from '../defs/config';
import { SymbolId } from '../defs/symbol';
import { PhaseId } from '../defs/types';
import {
  BasicBlockEntity,
  BlockEntity,
  despawnBlockEntity,
  spawnBlockEntity,
} from '../entities/BlockEntity';
import { SessionEntity } from '../entities/SessionEntity';
import type { GameScene } from '../GameScene';
import { blockIsBinded, blockIsFrozen } from '../util/blockTools';
import { Match, matchFindGroup } from '../util/matchTools';

/*
    handles basic block specifics
*/
export class BasicBlockSystem implements System {
  // constants
  //-------------------------------------------------------------------------
  public static readonly NAME = 'blockBasic';
  public static Queries: QueriesObject = {
    block: {
      components: [BlockComponent, BasicBlockComponent],
      added: true,
    },
    phase: {
      components: [PhaseComponent],
      modified: true,
    },
  };

  // fields
  //-------------------------------------------------------------------------
  // injected
  public scene!: GameScene;
  public queries!: QueryResults;

  private _lastPhase!: PhaseId;

  // impl
  //-------------------------------------------------------------------------
  public async turnRandomBlockInto(id: BlockId) {
    const actives = this.getActiveBasicBlocks();
    const block = randomItem(actives) as BlockEntity;
    return this.turnBlockInto(block, id);
  }

  public async turnBlockInto(block: BlockEntity, id: BlockId) {
    const position = block.c.position.mapPosition;
    despawnBlockEntity(this.scene, block, false);
    const powerBlock = spawnBlockEntity(this.scene, { id }, position);
    await new PowerBlockSpawnEffect(
      powerBlock,
      block.c.block.blockId,
      id,
    ).execute();

    return powerBlock;
  }

  public getActiveBasicBlocks() {
    // excluding frozen and binded blocks
    return (this.queries.block.entities as BasicBlockEntity[]).filter(
      (entity) =>
        !blockIsFrozen(this.scene, entity) &&
        !blockIsBinded(this.scene, entity),
    );
  }

  public getActiveBasicBlockIds(): BlockId[] {
    const ids: BlockId[] = [];
    this.getActiveBasicBlocks().forEach((entity: BasicBlockEntity) => {
      if (!ids.includes(entity.c.block.blockId))
        ids.push(entity.c.block.blockId);
    });
    return ids;
  }

  public addedToQuery(entity: BasicBlockEntity) {
    // update block symbol
    this._updateBlockSymbol(entity);
  }

  public updateBlockSymbols() {
    // update basic block symbols
    this.queries.block.forEach((entity: BasicBlockEntity) =>
      this._updateBlockSymbol(entity),
    );
  }

  public update() {
    const sessionEntity = this.queries.phase.first as SessionEntity;
    const phase = sessionEntity.c.phase.phase;

    // Continuosly update block symbols during movements
    if (this._lastPhase !== phase) {
      this._lastPhase = phase;
      this.updateBlockSymbols();
    }
  }

  // private: actions
  //-------------------------------------------------------------------------
  private _updateBlockSymbol(entity: BasicBlockEntity) {
    // get match group, else just this entity
    let group = matchFindGroup(this.scene, entity);
    if (!group) group = { matches: [{ base: entity }], neighbors: [] };

    // get power block symbol given match count else restore to original symbol
    const newSymbolId =
      this._getPowerMatch(group.matches) ||
      (blockPropsMap[entity.c.block.blockId] as BasicBlockProps).symbol;

    // change all matching symbols to found symbol id
    for (const match of group.matches) {
      match.base.c.blockBasic.symbolId = newSymbolId;
    }
  }

  // private: support
  //-------------------------------------------------------------------------
  private _getPowerMatch(match: Match[]): SymbolId | undefined {
    let powerBlockId!: PowerBlockType;
    let highCount = 0;

    // find highest matching count and corresponding power block id
    for (const [id, count] of Object.entries(config.blocks.basic.powerMatch)) {
      if (count >= highCount && count <= match.length) {
        powerBlockId = id as PowerBlockType;
        highCount = count;
      }
    }

    if (!powerBlockId) return undefined;

    const color = (match[0].base.c.block.props as BasicBlockProps).color;
    return `${powerBlockId}_${color}`;
  }
}
